// app/src/lib/scale/rolodexClaim.js — anonymous-rolodex signup handoff.
//
// Step 2 of the Kanjo Free Tools flow (Phase 2 of the spec). The marketing
// site's anonymous rolodex (IndexedDB on getkanjo.com) is bundled, POSTed
// to /v1/rolodex-stash, and the resulting claim token rides through Cognito
// signup. After the new user is signed in, the PWA reads the token from
// sessionStorage and calls /v1/rolodex-claim to retrieve the payload.

import { getConfig } from '../config.js';
import { getAccessToken } from '../auth/cognitoClient.js';

const PENDING_KEY = 'kanjoPendingRolodexClaim';
const CLAIM_EMAIL_KEY = 'kanjoClaimEmail';

/**
 * Stash the current anonymous rolodex on the server for cross-origin
 * handoff. Called from the marketing-site landing page BEFORE redirecting
 * to the PWA. (Imported here so the PWA can also re-trigger it during
 * tests; the marketing site has its own minimal implementation in
 * getkanjo.com/p/index.html.)
 *
 * @param {string} payload — JSON string of the anonymous-rolodex contents
 * @returns {Promise<{ claimToken: string, expiresIn: number }>}
 */
export async function stashAnonymousRolodex(payload) {
  const cfg = getConfig();
  const res = await fetch(`${cfg.apiBaseUrl}/v1/rolodex-stash`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ payload }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const e = new Error(body.error || `stash ${res.status}`);
    e.status = res.status;
    throw e;
  }
  return res.json();
}

/**
 * Claim a previously-stashed payload after sign-in. Single-use — the
 * server deletes the row before returning, so the same token can't be
 * replayed.
 *
 * @param {string} token — claim token UUID
 * @returns {Promise<string>} the original payload (JSON string)
 */
export async function claimRolodex(token) {
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('not signed in');
  const res = await fetch(`${cfg.apiBaseUrl}/v1/rolodex-claim?token=${encodeURIComponent(token)}`, {
    headers: { authorization: `Bearer ${at}` },
  });
  if (res.status === 404 || res.status === 410) {
    const body = await res.json().catch(() => ({}));
    const e = new Error(body.error || 'token gone');
    e.code = res.status === 410 ? 'expired' : 'not_found';
    throw e;
  }
  if (!res.ok) {
    const e = new Error(`claim ${res.status}`); e.status = res.status; throw e;
  }
  const body = await res.json();
  return body.payload;
}

/**
 * Pre-mount: detect ?rolodex_claim=<token> in the page URL and stash it
 * in sessionStorage so the post-sign-in claim handler can pick it up
 * after the Cognito round-trip. Strips the param from the URL so it
 * doesn't leak into history or refresh.
 *
 * Returns true if a token was captured (caller may want to route the
 * user to /auth proactively).
 */
export function capturePendingClaimFromUrl() {
  try {
    const u = new URL(window.location.href);
    const token = u.searchParams.get('rolodex_claim');
    if (!token) return false;
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(token)) return false;
    sessionStorage.setItem(PENDING_KEY, token);
    // The /p handoff also passes the email the visitor already gave, so the
    // Cognito sign-up form can be pre-filled (login_hint) instead of asking
    // for it again. Validate before trusting it as a hint.
    const email = u.searchParams.get('email');
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      try { sessionStorage.setItem(CLAIM_EMAIL_KEY, email.toLowerCase()); } catch { /* private mode */ }
    }
    u.searchParams.delete('rolodex_claim');
    u.searchParams.delete('email');
    window.history.replaceState({}, '', u.toString());
    return true;
  } catch { return false; }
}

/** The email handed off from the /p rolodex flow, for pre-filling sign-up.
 *  Empty string when none was passed. */
export function getPendingClaimEmail() {
  try { return sessionStorage.getItem(CLAIM_EMAIL_KEY) || ''; } catch { return ''; }
}

/**
 * Post-sign-in: if a claim token was stashed, fetch the payload, write
 * each contact to the maintenanceProviders Dexie store under the now-
 * known userId, and clear the pending token. Idempotent: a second call
 * is a no-op once the token has been cleared (or a server-side 404 if
 * it was claimed elsewhere).
 *
 * Each contact gets a fresh providerId (UUID), so re-claiming on a
 * second device would create duplicates — by design, the token is
 * single-use server-side so this can't actually happen.
 *
 * Returns the number of contacts written (0 if no pending token).
 */
export async function applyPendingClaimIfAny(db) {
  const token = sessionStorage.getItem(PENDING_KEY);
  if (!token) return 0;

  const claims = await import('../auth/cognitoClient.js').then((m) => m.currentClaims());
  if (!claims?.sub) return 0;

  let payloadStr;
  try {
    payloadStr = await claimRolodex(token);
  } catch (e) {
    // Clear on terminal errors so we don't loop on every mount.
    if (e.code === 'expired' || e.code === 'not_found') {
      sessionStorage.removeItem(PENDING_KEY);
    }
    console.warn('[rolodex] claim failed', e?.message || e);
    return 0;
  }

  let contacts = [];
  try {
    const parsed = JSON.parse(payloadStr);
    contacts = Array.isArray(parsed) ? parsed : [];
  } catch {
    sessionStorage.removeItem(PENDING_KEY);
    return 0;
  }

  let saved = 0;
  for (const c of contacts) {
    if (!c || typeof c !== 'object') continue;
    const name = String(c.name || '').trim();
    const category = String(c.category || '').trim();
    const phone = String(c.phone || '').trim();
    if (!name || !category || !phone) continue;
    await db.maintenanceProviders.put({
      providerId: crypto.randomUUID(),
      userId: claims.sub,
      name,
      category,
      phone,
      email: String(c.email || ''),
      website: String(c.website || ''),
      address: String(c.address || ''),
      source: 'rolodex-handoff',
      createdAt: Date.now(),
    });
    saved++;
  }

  sessionStorage.removeItem(PENDING_KEY);
  return saved;
}

export const PENDING_CLAIM_KEY = PENDING_KEY;
